<script setup lang="ts">
import { ref, type PropType, computed } from 'vue';
import FileUpload, { type FileUploadUploaderEvent } from 'primevue/fileupload';
import mime from 'mime';
import { type FileConfig, type FormField, BlobModel } from '@/models';
import { useEntryStore } from '@/stores/entry';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app';
import { prepareToast } from '@/models/toast';
import { heicTo, isHeic } from 'heic-to';

const { t: $t } = useI18n();

const entryStore = useEntryStore();
const appStore = useAppStore();

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  previewMode: {
    type: Boolean
  }
});

const localField = ref<FormField>(props.field);

const customFileUploader = async (event: FileUploadUploaderEvent) => {
  const files = event.files as File[];

  const selectedFiles : BlobModel[] = entryStore.selectedFiles[`${localField.value.identifier}`] ?? [];
  for (const file of files) {
    let uploadFile: File | Blob = file!;
    let uploadFileName = file.name;
    let uploadFileType = file.type;
    let convertedFile = false;
    const fileTypeLower = file.type.toLowerCase();
    if (await isHeic(file) || fileTypeLower === 'image/heic' || fileTypeLower === 'image/heif') {
      try {
        const conversionResult = await heicTo({
          blob: file,
          type: 'image/jpeg',
          quality: 0.8
        });
        uploadFile = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
        uploadFileName = uploadFileName.replace(/\.(heic|heif)$/i, '.jpeg');
        uploadFileType = 'image/jpeg';
        convertedFile = true;
      } catch (conversionError) {
        // Optionally, handle the error, e.g., by skipping the file or showing a message
        appStore.addToast(prepareToast('error', $t('file_conversion_error'), `${uploadFileName}: ${$t('heic_conversion_failed')}`));
        continue;
      }
    }
    selectedFiles.push({
      name: uploadFileName,
      contentType: uploadFileType,
      identifier: '',
      size: uploadFile.size,
      file: uploadFile as File,
      progress: 0,
      originalFile: file,
      convertedFile
    });
  }
  if (!entryStore.selectedFiles) {
    entryStore.selectedFiles = {};
    entryStore.selectedFiles[`${localField.value.identifier}`] = [];
  }
  entryStore.selectedFiles![`${localField.value.identifier}`] = selectedFiles;
  await entryStore.getSignedUrl(localField.value.identifier);
};

const acceptedExtensions = computed(() => {
  const allowedExtensionsList = localField.value.options.allowedExtensions ?
    ((localField.value.options.allowedExtensions as FileConfig).value as string)
      ?.split(',').map(extension => extension.trim()) : [];

  const mimeTypes: string[] = [];
  allowedExtensionsList.forEach(extension => {
    const mimeType = mime.getType(extension);
    if (mimeType) {
      mimeTypes.push(mimeType);
    }
  });
  return mimeTypes.join(',');
});

const maximumFileNumber = computed(() => {
  if ((localField.value.options.maximumFileNumber as FileConfig)?.value && entryStore.selectedFiles![localField.value.identifier]) {
    return parseInt(`${(localField.value.options.maximumFileNumber as FileConfig)!.value!}`) -
      entryStore.selectedFiles![localField.value.identifier].length;
  }
  return undefined;
});

const downloadFile = (file: BlobModel) => {
  entryStore.downloadBlob(file.identifier, file.name);
};

const deleteFile = (file: BlobModel) => {
  entryStore.deleteFile(localField.value.identifier, file.identifier);
};
</script>

<template>
  <div class="item-root">
    <div class="file-upload">
      <FileUpload @uploader="customFileUploader"
        :multiple="true"
        :fileLimit="maximumFileNumber"
        :invalidFileLimitMessage="
          `Maximum number of files exceeded, limit is ${(localField.options.maximumFileNumber as FileConfig)?.value} at most.`
        "
        :auto="true"
        :custom-upload="true"
        :showUploadButton="false"
        :showCancelButton="false"
        :accept="acceptedExtensions"
        mode="advanced"
        v-if="!previewMode"
        :maxFileSize="((localField.options.maximumSize as FileConfig)?.value as number) * 1048576">
        <template #empty>
          <div>{{ $t('drag_drop_files') }}</div>
        </template>
      </FileUpload>
      <div class="config-details" v-if="!previewMode">
        {{ (localField.options.maximumSize as FileConfig)?.value ?
          `${$t('size_limit')} ${(localField.options.maximumSize as FileConfig)?.value} MB.` : '' }}
        {{ acceptedExtensions != '' ? 
          `${$t('allowed_types')} ${(localField.options.allowedExtensions as FileConfig).value}.` : '' }}
        {{ (localField.options.maximumFileNumber as FileConfig)?.value ?
          `${$t('max_files_allowed')} ${(localField.options.maximumFileNumber as FileConfig)?.value}` : '' }}
      </div>

      <div class="upload-files" v-if="entryStore.selectedFiles">
        <div class="file" v-for="file in entryStore.selectedFiles![localField.identifier]" :key="file.name">
          <div class="thumbnail">
            <div class="progress" v-if="!file.storedAt">
              {{ `${$t('uploading')}` }}: {{ file.progress }}%
            </div>
            <FontAwesomeIcon :icon="['far', 'file']" v-if="file.storedAt && !file.hasThumbnail" class="file-icon"/>
            <img :src="file.thumbnailKey" class="thumbnail-img" v-if="file.storedAt && file.hasThumbnail"/>
          </div>
          <div class="thumbnail-details">
            <div class="name">{{ file.name }}</div>
            <div class="size">{{ file.size }} bytes</div>
          </div>
          <div class="file-actions">
            <div class="icon-button" v-ripple @click="downloadFile(file)">
              <FontAwesomeIcon :icon="['far', 'download']" class="icon"/>
            </div>
            <div class="icon-button"
              v-ripple
              v-if="!previewMode"
              @click="deleteFile(file)">
              <FontAwesomeIcon :icon="['fal', 'trash']"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.upload-files {
  display: grid;
  padding: 24px 0px;
  grid-template-columns: repeat(5, 136px);
  grid-gap: 32px;
  font-size: 12px;

  .file {
    cursor: pointer;

    .thumbnail {
      height: 100px;
      width: 136px;
      margin-bottom: 12px;
      border-radius: 4px;
      background-color: #fafbfc;
      box-shadow: inset 0 0 0 1px #b8c2cc;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        box-shadow: 0 0 0 4px #bfdcff;
      }
    }

    .file-icon {
      font-size: 50px;
      color: #b8c2cc;
    }

    .thumbnail-img {
      height: 100px;
      width: 136px;
      object-fit: cover;
      border-radius: 4px;
      box-shadow: inset 0 0 0 1px #b8c2cc;
    }

    .thumbnail-details {
      .name {
        color: #272a2e;
        overflow: hidden;
        white-space: nowrap;
        font-weight: 400;
        text-overflow: ellipsis;
      }

      .size {
        color: #828e99;
        font-weight: 400;
      }
    }

    .icon-button {
      display: none;
    }

    .file-actions {
      display: flex;
      justify-content: flex-end;
    }

    &:hover {
      .name, .size {
        display: none;
      }

      .icon-button {
        font-size: 14px;
        margin-left: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 30px;
        width: 30px;
        border-radius: 50%;

        &:hover {
          background-color: rgba(0, 0, 0, 0.08);
        }
      }
    }
  }
}

</style>
